// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { authStore } from "../lib/api";
import { useApp } from "../context/AppContext";
import { financeStats, fmtMoney, fmtMoneyShort, teamDistribution, phaseDistribution } from "../lib/appUtils";
import { riskSummary } from "../lib/schedule";
import { TEAMS, PAY_STAGES, COMPLETED_INDEX } from "../data/constants";
import { Crown, AlertCircle, AlertTriangle, ShieldCheck, CheckCircle2, HardHat, LineChart as LineIcon, Camera, FileDown, Mail, Target, Settings2, GripVertical, Eye, EyeOff, X, ChevronUp, ChevronDown, ArrowRight, PackageCheck } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell, Legend, LineChart, Line, LabelList } from "recharts";
import SalesLeaderboard from "../components/SalesLeaderboard";
import ProjectMap from "../components/ProjectMap";
import BranchPerformance from "../components/BranchPerformance";
import LowStockCard from "../components/LowStockCard";
import LaborCostCard from "../components/LaborCostCard";
import InvoiceGapCard from "../components/InvoiceGapCard";
import { FinanceKpiRow, AIDailyBrief, CashflowChart, ARAgingCard, SettleBacklogCard } from "../components/CEOStrips";
import { PeriodPicker, periodLabel, periodRange, inPeriod } from "../components/PeriodPicker";
import { fmtD } from "../lib/date";
import { CEOApprovalSummary } from "./AdministrativeApprovalCenter";
import { OperatingCEOCard } from "../components/finance/OperatingApprovals";
import { SubcontractSigningInbox } from "../components/SubcontractSigning";

const SALES_COLORS = ["#4F6BFF", "#7B5CFF", "#06b6d4", "#16a34a", "#d97706", "#ec4899"];

const SECTION_DEFS = [
  { key: "kpis", zh: "财务 KPI 总览", en: "Finance KPIs" },
  { key: "brief", zh: "AI 今日简报", en: "AI Daily Brief" },
  { key: "cashflow", zh: "现金流走势", en: "Cash Flow" },
  { key: "ar_aging", zh: "AR 账龄分析", en: "AR Aging" },
  { key: "settle_backlog", zh: "分公司待结算排行", en: "Settlement Backlog" },
  { key: "branches", zh: "分公司业绩榜", en: "Branch Scoreboard" },
  { key: "health", zh: "AI 销售预测健康度", en: "Forecast Health" },
  { key: "map", zh: "项目地图", en: "Project Map" },
  { key: "collections", zh: "收款分析图表", en: "Collections Charts" },
  { key: "leaderboard", zh: "销售排行榜", en: "Sales Leaderboard" },
  { key: "briefing", zh: "月度经营简报", en: "Monthly Briefing" },
  { key: "perf_trend", zh: "销售业绩趋势", en: "Performance Trend" },
  { key: "engineering", zh: "工程管理总览", en: "Construction Overview" },
  { key: "overpay", zh: "超付合同 Top 榜", en: "Overpayment Board" },
  { key: "lowstock", zh: "仓库低库存提醒", en: "Low Stock" },
  { key: "labor_cost", zh: "用工成本（美国 + 中国）", en: "Labor Cost (US + China)" },
  { key: "invoice_gap", zh: "开票未收（QB 发票 vs 实收）", en: "Invoiced but Unpaid (QB)" },
];
const DEFAULT_ORDER = SECTION_DEFS.map((s) => s.key);

function AdMaterialApprovalCard() {
  const { api, lang, canAction } = useApp();
  const canApprove = canAction("/crm/ad-materials", "approve") === true;
  const [count, setCount] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!canApprove) return;
    let active = true;
    api.get("/crm/ad-materials/requests?status=pending_ceo&queue=approvable")
      .then(({ data }) => { if (active) setCount((data || []).length); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [api, canApprove]);
  if (!canApprove) return null;
  const zh = lang === "zh";
  return <Link to="/crm/ad-materials?tab=approvals" data-testid="ceo-ad-material-approval-card"
    className="flex min-h-11 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-brand-300 focus-visible:ring-2 focus-visible:ring-brand-400">
    <PackageCheck className="h-6 w-6 shrink-0 text-brand-500" />
    <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-slate-900">{zh ? "广告物料 · 采购预算待审" : "Ad materials · Purchase requests"}</div>
      <p className="text-xs text-slate-500">{zh ? "核对明细与报价，批准后生成采购单" : "Review items and quotes before creating a purchase order"}</p></div>
    <span className="shrink-0 text-sm font-semibold text-brand-600">{error ? (zh ? "查看" : "Open") : count ?? "…"} {zh ? "待审批" : "pending"}</span>
    <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />
  </Link>;
}

export default function CEODashboard() {
  const { t, lang, projects, user, api } = useApp();
  const [snaps, setSnaps] = useState([]);
  const [snapping, setSnapping] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [briefEmailing, setBriefEmailing] = useState(false);
  const [health, setHealth] = useState(null);
  const [overpay, setOverpay] = useState([]);
  const [layout, setLayout] = useState({ order: DEFAULT_ORDER, hidden: [] });
  const [showLayout, setShowLayout] = useState(false);
  const [period, setPeriod] = useState(() => localStorage.getItem("ceo_period") || "all");
  useEffect(() => { localStorage.setItem("ceo_period", period); }, [period]);
  const range = periodRange(period);
  const isAll = !range;
  useEffect(() => { api.get("/finance/overpayment-alerts").then(({ data }) => setOverpay(data || [])).catch(() => setOverpay([])); }, [api]);
  useEffect(() => {
    api.get("/ceo/layout").then(({ data }) => {
      const savedOrder = Array.isArray(data.order) && data.order.length ? data.order : DEFAULT_ORDER;
      // Merge in any new SECTION_DEFS keys that were added after the layout was saved
      const missing = DEFAULT_ORDER.filter((k) => !savedOrder.includes(k));
      setLayout({ order: [...savedOrder.filter((k) => DEFAULT_ORDER.includes(k)), ...missing], hidden: data.hidden || [] });
    }).catch(() => {});
  }, [api]);

  const loadSnaps = async () => { try { const { data } = await api.get("/performance/snapshots"); setSnaps(data); } catch {} };

  const exportExecReport = () => {
    const token = authStore.get("ali_token") || "";
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const a = document.createElement("a");
    a.href = `${BACKEND}/api/ceo/executive-report-pdf?auth=${token}&lang=${lang}`;
    a.target = "_blank"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };

  useEffect(() => {
    loadSnaps();
    api.get("/crm/forecast/health").then((r) => setHealth(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const takeSnapshot = async () => {
    setSnapping(true);
    try { const { data } = await api.post("/performance/snapshot"); alert(lang === "zh" ? `已保存 ${data.month} 业绩快照（${data.count} 名销售）` : `Saved ${data.month} snapshot (${data.count} sales)`); await loadSnaps(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
    setSnapping(false);
  };

  const downloadReport = async () => {
    try {
      const res = await api.get("/performance/report-pdf", { params: { lang }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "sales_report.pdf"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { alert(lang === "zh" ? "月报生成失败" : "Report failed"); }
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/performance/report-email", { lang });
      const okCount = (data.results || []).filter((r) => r.ok).length;
      alert(lang === "zh"
        ? `已向 ${data.recipients.length} 位管理层发送月报，成功 ${okCount} 封${okCount < data.recipients.length ? "（其余因SendGrid额度未送达）" : ""}。`
        : `Sent to ${data.recipients.length}; ${okCount} delivered.`);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    setEmailing(false);
  };

  const downloadBriefing = async () => {
    try {
      const res = await api.get("/reports/monthly-briefing-pdf", { params: { lang }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "monthly_briefing.pdf"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { alert(lang === "zh" ? "简报生成失败" : "Briefing failed"); }
  };

  const emailBriefing = async () => {
    setBriefEmailing(true);
    try {
      const { data } = await api.post("/reports/monthly-briefing-email", { lang });
      const okCount = (data.results || []).filter((r) => r.ok).length;
      alert(lang === "zh"
        ? `已向 ${data.recipients.length} 位管理层发送经营简报，成功 ${okCount} 封${okCount < data.recipients.length ? "（其余因SendGrid额度未送达）" : ""}。`
        : `Sent briefing to ${data.recipients.length}; ${okCount} delivered.`);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    setBriefEmailing(false);
  };

  // Build trend chart: months as X, one line per salesperson (signed cumulative)
  const months = Array.from(new Set(snaps.map((s) => s.month))).sort();
  const salesNames = Array.from(new Set(snaps.map((s) => s.salesName)));
  const trendData = months.map((m) => {
    const row = { month: m };
    salesNames.forEach((n) => { const rec = snaps.find((s) => s.month === m && s.salesName === n); row[n] = rec ? rec.signed : 0; });
    return row;
  });

  // receivable by team（有时间范围时：期内回款 / 期内新增应收）
  const payDate = (pay, p) => pay.paidAt || pay.date || pay.statusAt || p.signedDate || "";
  const byTeam = TEAMS.map((tm, i) => {
    const list = projects.filter((p) => p.team === tm.id);
    const s = isAll ? financeStats(list) : (() => {
      let collected = 0, receivable = 0;
      list.forEach((p) => (p.payments || []).forEach((pay) => {
        if (pay.status === "paid") { if (inPeriod(payDate(pay, p), range)) collected += pay.amount || 0; }
        else if (inPeriod(pay.statusAt || pay.date || p.signedDate, range)) receivable += pay.amount || 0;
      }));
      return { collected, receivable, pending: 0 };
    })();
    return { name: lang === "zh" ? tm.zh : tm.name, collected: s.collected, receivable: s.receivable, pending: s.pending, color: ["#4F6BFF", "#7B5CFF", "#06b6d4", "#10b981", "#f59e0b"][i % 5] };
  });

  // collection by milestone stage (company-wide)
  const byStage = PAY_STAGES.map((st) => {
    let paid = 0, due = 0;
    projects.forEach((p) => p.payments.forEach((pay) => {
      if (pay.stage !== st.key) return;
      if (pay.status === "paid") { if (inPeriod(payDate(pay, p), range)) paid += pay.amount; }
      else if (isAll || inPeriod(pay.statusAt || pay.date || p.signedDate, range)) due += pay.amount;
    }));
    return { name: lang === "zh" ? st.zh : st.en, paid, due };
  });

  // engineering management overview
  const risk = riskSummary(projects);
  const completedCount = projects.filter((p) => p.phaseIndex >= COMPLETED_INDEX).length;
  const constructionCount = projects.filter((p) => p.stage !== "design" && p.phaseIndex >= 0 && p.phaseIndex < COMPLETED_INDEX).length;
  const phases = phaseDistribution(projects);
  const teamProg = teamDistribution(projects).filter((x) => x.count > 0);

  const renderers = {
    kpis: () => <FinanceKpiRow period={period} />,
    brief: () => <AIDailyBrief period={period} />,
    cashflow: () => <CashflowChart period={period} />,
    ar_aging: () => <ARAgingCard />,
    settle_backlog: () => <SettleBacklogCard />,
    branches: () => <BranchPerformance period={period} onPeriodChange={setPeriod} />,
    health: () => health && (() => {
      const cfg = { green: { bg: "from-emerald-600 to-green-700", label: lang === "zh" ? "达标" : "On Target", dot: "bg-emerald-300" },
                    amber: { bg: "from-amber-500 to-orange-600", label: lang === "zh" ? "预警" : "Warning", dot: "bg-amber-200" },
                    red: { bg: "from-red-600 to-rose-700", label: lang === "zh" ? "缺口告警" : "Shortfall", dot: "bg-red-300" } }[health.status];
      return (
        <div data-testid="ceo-forecast-health" className={`rounded-xl p-5 text-white bg-gradient-to-r ${cfg.bg} flex flex-wrap items-center justify-between gap-4`}>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center"><Target className="h-6 w-6" /></div>
            <div>
              <p className="text-white/80 text-xs font-medium flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${cfg.dot} animate-pulse`} />{lang === "zh" ? "AI 销售预测健康度" : "AI Forecast Health"} · {cfg.label}</p>
              <p className="text-2xl font-extrabold mt-0.5" data-testid="ceo-forecast-pct">{health.pct}% <span className="text-sm font-medium text-white/80">{lang === "zh" ? "目标达成率" : "of quota"}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-right">
            <div><p className="text-white/70 text-[11px]">{lang === "zh" ? "加权预测" : "Weighted"}</p><p className="text-lg font-bold">{fmtMoneyShort(health.weightedPipeline)}</p></div>
            <div><p className="text-white/70 text-[11px]">{lang === "zh" ? "月度目标" : "Quota"}</p><p className="text-lg font-bold">{fmtMoneyShort(health.monthlyQuota)}</p></div>
            <div><p className="text-white/70 text-[11px]">{lang === "zh" ? "缺口" : "Shortfall"}</p><p className="text-lg font-bold" data-testid="ceo-forecast-shortfall">{health.shortfall > 0 ? fmtMoneyShort(health.shortfall) : (lang === "zh" ? "无" : "None")}</p></div>
          </div>
        </div>
      );
    })(),
    map: () => <ProjectMap />,
    collections: () => (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">{lang==="zh" ? `各小组收款 / 应收${isAll ? "" : `（${periodLabel(period, lang === "zh")}）`}` : `Collections vs Receivables by Team${isAll ? "" : ` · ${periodLabel(period, false)}`}`}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byTeam} margin={{ top: 6, right: 6, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="collected" name={t("collected")} fill="#16a34a" radius={[5,5,0,0]} />
              <Bar dataKey="receivable" name={t("receivable")} fill="#4F6BFF" radius={[5,5,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">{lang==="zh" ? `各收款节点已收/待收${isAll ? "" : `（${periodLabel(period, true)}）`}` : `Collection by Milestone${isAll ? "" : ` · ${periodLabel(period, false)}`}`}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={byStage} margin={{ top: 6, right: 6, left: -8, bottom: 40 }}>
              <defs>
                <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#16a34a" stopOpacity={0.5}/><stop offset="100%" stopColor="#16a34a" stopOpacity={0.05}/></linearGradient>
                <linearGradient id="gDue" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#d97706" stopOpacity={0.5}/><stop offset="100%" stopColor="#d97706" stopOpacity={0.05}/></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} angle={-30} textAnchor="end" interval={0} />
              <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="paid" name={t("collected")} stroke="#16a34a" fill="url(#gPaid)" strokeWidth={2} />
              <Area type="monotone" dataKey="due" name={t("pendingCollection")} stroke="#d97706" fill="url(#gDue)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    ),
    leaderboard: () => <SalesLeaderboard />,
    briefing: () => (
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="briefing-card">
        <div>
          <h3 className="font-semibold text-white flex items-center gap-2"><FileDown className="h-4 w-4 text-red-400" />{lang === "zh" ? "月度经营简报" : "Monthly Operations Briefing"}</h3>
          <p className="text-slate-400 text-xs mt-1">{lang === "zh" ? "一份 PDF 汇总施工效率趋势 + 销售业绩排行，可下载或群发管理层" : "One PDF combining efficiency trend + sales ranking"}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="download-briefing-btn" onClick={downloadBriefing}
            className="max-md:min-h-11 max-md:min-w-11 inline-flex items-center gap-1.5 text-sm text-white border border-slate-600 hover:border-brand-400 hover:text-brand-300 rounded-lg px-3 py-2 transition-colors">
            <FileDown className="h-4 w-4" />{lang === "zh" ? "下载简报PDF" : "Download PDF"}
          </button>
          <button data-testid="email-briefing-btn" onClick={emailBriefing} disabled={briefEmailing}
            className="max-md:min-h-11 max-md:min-w-11 inline-flex items-center gap-1.5 text-sm text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 rounded-lg px-3 py-2 transition-colors">
            <Mail className="h-4 w-4" />{briefEmailing ? (lang === "zh" ? "发送中…" : "Sending…") : (lang === "zh" ? "发管理层" : "Email Mgmt")}
          </button>
        </div>
      </div>
    ),
    perf_trend: () => (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="perf-trend">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <LineIcon className="h-4 w-4 text-brand-500" />
          <h3 className="font-semibold text-slate-900">{lang === "zh" ? "销售业绩趋势（月度快照）" : "Sales Performance Trend (Monthly Snapshots)"}</h3>
          <button data-testid="take-snapshot-btn" onClick={takeSnapshot} disabled={snapping}
            className="max-md:min-h-11 max-md:min-w-11 ml-auto inline-flex items-center gap-1 text-xs text-slate-600 border border-slate-200 hover:border-brand-300 hover:text-brand-500 disabled:opacity-50 rounded-lg px-2.5 py-1.5 transition-colors">
            <Camera className="h-3.5 w-3.5" />{snapping ? (lang === "zh" ? "生成中…" : "Saving…") : (lang === "zh" ? "生成本月快照" : "Snapshot This Month")}
          </button>
          <button data-testid="download-report-btn" onClick={downloadReport}
            className="max-md:min-h-11 max-md:min-w-11 inline-flex items-center gap-1 text-xs text-slate-600 border border-slate-200 hover:border-brand-300 hover:text-brand-500 rounded-lg px-2.5 py-1.5 transition-colors">
            <FileDown className="h-3.5 w-3.5" />{lang === "zh" ? "下载月报PDF" : "Report PDF"}
          </button>
          <button data-testid="email-report-btn" onClick={emailReport} disabled={emailing}
            className="max-md:min-h-11 max-md:min-w-11 inline-flex items-center gap-1 text-xs text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 rounded-lg px-2.5 py-1.5 transition-colors">
            <Mail className="h-3.5 w-3.5" />{emailing ? (lang === "zh" ? "发送中…" : "Sending…") : (lang === "zh" ? "发给管理层" : "Email Mgmt")}
          </button>
        </div>
        {trendData.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">{lang === "zh" ? "暂无快照数据，点击右上角生成本月快照。" : "No snapshots yet."}</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData} margin={{ top: 6, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {salesNames.map((n, i) => (
                <Line key={n} type="monotone" dataKey={n} stroke={SALES_COLORS[i % SALES_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    ),
    engineering: () => (
      <div className="space-y-6">
        <div className="flex items-center gap-2 pt-2">
          <HardHat className="h-5 w-5 text-slate-700" />
          <h2 className="text-lg font-bold text-slate-900">{lang === "zh" ? "工程管理总览" : "Construction Management Overview"}</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <EngStat icon={HardHat} label={lang === "zh" ? "工程总数（在建）" : "Total Construction"} value={constructionCount} tint="#0ea5e9" />
          <EngStat icon={AlertTriangle} label={lang === "zh" ? "高风险项目" : "High Risk"} value={risk.high.length} tint="#e11b22" />
          <EngStat icon={AlertCircle} label={lang === "zh" ? "需关注" : "At Risk"} value={risk.medium.length} tint="#d97706" />
          <EngStat icon={ShieldCheck} label={lang === "zh" ? "进度正常" : "On Track"} value={risk.low.length} tint="#16a34a" />
          <EngStat icon={CheckCircle2} label={lang === "zh" ? "已完成" : "Completed"} value={completedCount} tint="#059669" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">{lang === "zh" ? "各阶段项目分布" : "Projects by Phase"}</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={phases} margin={{ top: 18, right: 6, left: -18, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey={lang === "zh" ? "zh" : "en"} tick={{ fontSize: 11, fill: "#64748b" }} angle={-25} textAnchor="end" interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} data-testid="ceo-phase-bars">
                  {phases.map((p, i) => <Cell key={i} fill={p.color} />)}
                  <LabelList dataKey="count" position="top" formatter={(v) => (v > 0 ? v : "")} style={{ fontSize: 12, fontWeight: 700, fill: "#334155" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">{lang === "zh" ? "各小组平均进度" : "Avg Progress by Team"}</h3>
            <div className="space-y-4 mt-6">
              {teamProg.map((tm, i) => (
                <div key={tm.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-slate-700">{lang === "zh" ? tm.zh : tm.name} <span className="text-slate-400">· {tm.count} {t("project")}</span></span>
                    <span className="text-slate-500">{tm.avgProgress}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${tm.avgProgress}%`, background: ["#4F6BFF", "#7B5CFF", "#06b6d4", "#10b981"][i % 4] }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
    overpay: () => (
      <div data-testid="ceo-overpay-topboard" className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <h3 className="font-semibold text-slate-900">{lang === "zh" ? "全公司超付合同 Top 榜" : "Company-wide Overpayment Leaderboard"}</h3>
          <span className="text-xs text-slate-400">· {lang === "zh" ? `${overpay.length} 个风险合同` : `${overpay.length} at risk`}</span>
        </div>
        {overpay.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">{lang === "zh" ? "暂无超付风险 ✓" : "No overpayment risk ✓"}</p>
        ) : (
          <div className="overflow-x-auto">
            <ResponsiveTable className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">{lang === "zh" ? "工种 / 项目" : "Trade / Project"}</th>
                  <th className="py-2 pr-2 text-right">{lang === "zh" ? "合同额" : "Contract"}</th>
                  <th className="py-2 pr-2 text-right">{lang === "zh" ? "已付" : "Paid"}</th>
                  <th className="py-2 pr-2 text-right">{lang === "zh" ? "进度" : "Progress"}</th>
                  <th className="py-2 pr-2 text-right">{lang === "zh" ? "超前" : "Over"}</th>
                </tr>
              </thead>
              <tbody>
                {overpay.slice(0, 10).map((a, i) => (
                  <tr key={a.subcontractId} data-testid={`ceo-overpay-row-${a.subcontractId}`} className="border-b border-slate-50">
                    <td className="py-2 pr-2 text-slate-400 font-bold">{i + 1}</td>
                    <td className="py-2 pr-2"><span className="font-medium text-slate-800">{a.tradeName}</span><span className="block text-[11px] text-slate-400 truncate max-w-[220px]">{a.address}{a.subcontractorName ? " · " + a.subcontractorName : ""}</span></td>
                    <td className="py-2 pr-2 text-right text-slate-600">{fmtMoneyShort(a.contractAmount)}</td>
                    <td className="py-2 pr-2 text-right text-slate-600">{Math.round(a.paidRatio * 100)}%</td>
                    <td className="py-2 pr-2 text-right text-slate-600">{Math.round(a.progressRatio * 100)}%<span className="block text-[10px] text-slate-400">{a.basis === "stage" ? (lang === "zh" ? "验收" : "insp.") : (lang === "zh" ? "阶段" : "phase")}</span></td>
                    <td className="py-2 pr-2 text-right"><span className={`font-bold px-2 py-0.5 rounded-full text-[11px] ${a.gap >= 1 ? "bg-red-100 text-red-700" : a.gap >= 0.6 ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>+{Math.round(a.gap * 100)}%</span></td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>
        )}
      </div>
    ),
    lowstock: () => <LowStockCard />,
    labor_cost: () => <LaborCostCard />,
    invoice_gap: () => <InvoiceGapCard />,
  };

  const visible = layout.order.filter((k) => !layout.hidden.includes(k) && renderers[k]);

  return (
    <div className="space-y-6">
      {/* CEO 移动办公入口：仅统计本人有权查看/处理的行政事项，审批与签署分别呈现。 */}
      <CEOApprovalSummary />
      <AdMaterialApprovalCard />
      <OperatingCEOCard />
      <SubcontractSigningInbox />
      <div className="flex flex-wrap items-center gap-2">
        <Crown className="h-6 w-6 text-brand-500" />
        <h1 className="text-2xl font-bold text-slate-900">{t("ceoBoard")}</h1>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="customize-layout-btn" onClick={() => setShowLayout(true)}
            className="max-md:min-h-11 max-md:min-w-11 inline-flex items-center gap-1.5 text-sm text-slate-600 border border-slate-200 hover:border-brand-300 hover:text-brand-500 rounded-lg px-3 py-2 transition-colors">
            <Settings2 className="h-4 w-4" />{lang === "zh" ? "自定义布局" : "Customize"}
          </button>
          <button data-testid="export-exec-report-btn" onClick={exportExecReport}
            className="max-md:min-h-11 max-md:min-w-11 inline-flex items-center gap-1.5 text-sm text-white bg-slate-900 hover:bg-slate-700 rounded-lg px-3 py-2 transition-colors">
            <FileDown className="h-4 w-4" />{lang === "zh" ? "导出经营快报 PDF" : "Executive Report PDF"}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 -mt-4" data-testid="ceo-period-bar">
        <p className="text-slate-500 text-sm">{lang === "zh" ? "全公司经营驾驶舱 · 财务/现金流/AI 简报/分公司业绩" : "Company cockpit · finance, cash flow, AI brief & branch performance"}</p>
        <div className="ml-auto flex items-center gap-2">
          {range && <span className="text-[11px] text-slate-400" data-testid="ceo-period-range">{fmtD(range.start)} ~ {fmtD(range.end)}</span>}
          <PeriodPicker value={period} onChange={setPeriod} zh={lang === "zh"} testid="ceo-period" />
        </div>
      </div>

      {visible.map((k) => <div key={k} data-testid={`ceo-section-${k}`}>{renderers[k]()}</div>)}

      {showLayout && <LayoutDialog lang={lang} api={api} layout={layout} onChange={setLayout} onClose={() => setShowLayout(false)} />}
    </div>
  );
}

function LayoutDialog({ lang, api, layout, onChange, onClose }) {
  const zh = lang === "zh";
  const [order, setOrder] = useState(layout.order);
  const [hidden, setHidden] = useState(layout.hidden);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const label = (k) => { const s = SECTION_DEFS.find((x) => x.key === k); return s ? (zh ? s.zh : s.en) : k; };
  const move = (from, to) => {
    if (from === to || from == null || to == null) return;
    setOrder((prev) => { const next = [...prev]; const [it] = next.splice(from, 1); next.splice(to, 0, it); return next; });
  };
  const toggle = (k) => setHidden((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/ceo/layout", { order, hidden });
      onChange({ order: data.order, hidden: data.hidden });
      onClose();
    } catch { /* ignore */ }
    setSaving(false);
  };
  const reset = () => { setOrder(DEFAULT_ORDER); setHidden([]); };
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" data-testid="layout-dialog" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-900">{zh ? "自定义看板布局" : "Customize Layout"}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{zh ? "拖动或点上下箭头排序 · 点击眼睛隐藏/显示区块" : "Drag or use arrows to reorder · toggle eye to hide/show"}</p>
          </div>
          <button data-testid="layout-close-btn" onClick={onClose} className="max-md:min-h-11 max-md:min-w-11 text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {order.map((k, i) => (
            <div key={k} draggable data-testid={`layout-row-${k}`}
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== i) { move(dragIdx, i); setDragIdx(i); } }}
              onDragEnd={() => setDragIdx(null)}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 cursor-grab active:cursor-grabbing transition-colors ${dragIdx === i ? "border-red-300 bg-red-50/50" : "border-slate-100 bg-slate-50/50 hover:border-slate-200"} ${hidden.includes(k) ? "opacity-45" : ""}`}>
              <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
              <span className="text-xs font-bold text-slate-400 w-5">{i + 1}</span>
              <span className="flex-1 min-w-0 text-sm font-medium text-slate-700">{label(k)}</span>
              {/* 手机上原生拖拽不可靠，箭头复用同一排序状态，仍需点保存才持久化。 */}
              <div className="flex flex-col md:hidden shrink-0">
                <button type="button" className="h-11 w-11 flex items-center justify-center disabled:opacity-30" disabled={i === 0} aria-label={`${zh ? "上移" : "Move up"} ${label(k)}`} onClick={() => move(i, i - 1)}><ChevronUp className="h-4 w-4" /></button>
                <button type="button" className="h-11 w-11 flex items-center justify-center disabled:opacity-30" disabled={i === order.length - 1} aria-label={`${zh ? "下移" : "Move down"} ${label(k)}`} onClick={() => move(i, i + 1)}><ChevronDown className="h-4 w-4" /></button>
              </div>
              <button data-testid={`layout-toggle-${k}`} onClick={() => toggle(k)}
                className={"max-md:min-h-11 max-md:min-w-11 " + (`shrink-0 ${hidden.includes(k) ? "text-slate-300 hover:text-slate-500" : "text-emerald-500 hover:text-emerald-700"}`)}
                title={hidden.includes(k) ? (zh ? "显示" : "Show") : (zh ? "隐藏" : "Hide")}>
                {hidden.includes(k) ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
          <button data-testid="layout-reset-btn" onClick={reset} className="max-md:min-h-11 max-md:min-w-11 text-sm text-slate-500 hover:text-red-600">{zh ? "恢复默认" : "Reset"}</button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="max-md:min-h-11 max-md:min-w-11 text-sm text-slate-500 px-3 py-2 rounded-lg hover:bg-slate-100">{zh ? "取消" : "Cancel"}</button>
            <button data-testid="layout-save-btn" onClick={save} disabled={saving}
              className="max-md:min-h-11 max-md:min-w-11 text-sm text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 rounded-lg px-4 py-2 font-medium">
              {saving ? (zh ? "保存中…" : "Saving…") : (zh ? "保存布局" : "Save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EngStat({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 font-medium">{label}</p>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: tint + "22" }}><Icon className="h-5 w-5" style={{ color: tint }} /></div>
      </div>
      <p className="text-2xl font-extrabold text-slate-900 mt-3">{value}</p>
    </div>
  );
}
